import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, TrendingUp, Eye, Calendar, BarChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/analytics/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load analytics. Please try again.');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-stone-900 mb-2">Website Analytics</h1>
              <p className="text-stone-600">Track your visitor statistics</p>
            </div>
            <button
              onClick={() => navigate('/admin/leads')}
              className="px-4 py-2 bg-stone-700 text-white rounded-lg hover:bg-stone-800 flex items-center gap-2"
            >
              <ArrowLeft size={18} />
              Back to Leads
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
              <p className="mt-4 text-stone-600">Loading analytics...</p>
            </div>
          ) : error ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={fetchStats}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Retry
                </button>
              </CardContent>
            </Card>
          ) : stats ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white text-3xl">{stats.total_views}</CardTitle>
                        <p className="text-blue-100 text-sm mt-1">Total Page Views</p>
                      </div>
                      <Eye size={40} className="text-blue-200 opacity-80" />
                    </div>
                  </CardHeader>
                </Card>

                <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white text-3xl">{stats.today_views}</CardTitle>
                        <p className="text-green-100 text-sm mt-1">Views Today</p>
                      </div>
                      <Calendar size={40} className="text-green-200 opacity-80" />
                    </div>
                  </CardHeader>
                </Card>

                <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white text-3xl">{stats.week_views}</CardTitle>
                        <p className="text-purple-100 text-sm mt-1">Views This Week</p>
                      </div>
                      <TrendingUp size={40} className="text-purple-200 opacity-80" />
                    </div>
                  </CardHeader>
                </Card>
              </div>

              {/* Popular Pages */}
              <Card className="mb-8">
                <CardHeader className="bg-gradient-to-r from-stone-50 to-white">
                  <div className="flex items-center gap-2">
                    <BarChart size={24} className="text-maroon-600" />
                    <CardTitle>Most Popular Pages</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {stats.popular_pages.length > 0 ? (
                    <div className="space-y-4">
                      {stats.popular_pages.map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-maroon-600 text-white rounded-full flex items-center justify-center font-bold">
                              {index + 1}
                            </div>
                            <span className="font-medium text-stone-900">{page.page}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Eye size={16} className="text-stone-500" />
                            <span className="text-lg font-bold text-maroon-600">{page.views}</span>
                            <span className="text-stone-500 text-sm">views</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-8">No page views yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-stone-50 to-white">
                  <CardTitle>Recent Page Views</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {stats.recent_views.length > 0 ? (
                    <div className="space-y-2">
                      {stats.recent_views.map((view, index) => (
                        <div key={index} className="flex items-center justify-between p-3 hover:bg-stone-50 rounded-lg transition">
                          <span className="text-stone-700">{view.page}</span>
                          <span className="text-sm text-stone-500">{formatDate(view.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-8">No recent activity</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
